import React, { useEffect, useMemo, useState } from 'react';
import { 
  FaSearch, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaTrashAlt,
  FaFilter, 
  FaImage, 
  FaEye, 
  FaStar, 
  FaRegStar, 
  FaUpload,
  FaTimes,
  FaFilePdf,
  FaSync,
  FaInfoCircle,
  FaBox
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { addItem, deleteItem, editItem, getAllItems, getItemDetails } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useAdminData } from '../../context/AdminDataContext';
import PageBanner from '../components/PageBanner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


export default function Products() {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    photo: '',
    amount: '',
    originalImages: [],
    originalFields: {
      name: '',
      description: '',
      category: '',
      price: '',
      amount: '',
    },
    // Local-only field: which admin segment/tab this product belongs to
    // 'all' | 'new' | 'trending' | 'sale'
    segment: 'all'
  });
  // Locally uploaded files (from computer) with names; do not auto-fill URL field
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(0);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [isLoadingAllItems, setIsLoadingAllItems] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isItemDetailModalOpen, setIsItemDetailModalOpen] = useState(false);
  const [itemDetails, setItemDetails] = useState(null);
  const [isLoadingItemDetails, setIsLoadingItemDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // all | new | trending | sale
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100; // Show up to 100 products per page to display all items
  const { user: currentUser, isAdmin, getToken } = useAuth();
  const { items: cachedItems, refresh, addNotification } = useAdminData();

  // Inline SVG placeholders to avoid network calls and onError loops
  const FALLBACK_IMG_48 = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="8" fill="%239ca3af">Image</text></svg>';
  const FALLBACK_IMG_40 = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="8" fill="%239ca3af">Image</text></svg>';
  const FALLBACK_IMG_192 = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="192" height="192"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="18" fill="%239ca3af">Image</text></svg>';

  const CLOUDINARY_CLOUD_NAME = 'djksfayfu';
  const CLOUDINARY_UPLOAD_PRESET = 'brand-decor';

  const sortByNewestFirst = (list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => (Number(b?.id) || 0) - (Number(a?.id) || 0));
  };

  // Normalize API photo field: fix cases like "image/upload/https://..." and accept data URLs
  const normalizePhotoValue = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('data:image')) return trimmed; // base64/data URL
    // If API returned a malformed cloudinary string like 'image/upload/https://...'
    if (trimmed.startsWith('image/upload/https://') || trimmed.startsWith('image/upload/http://')) {
      return trimmed.replace(/^image\/upload\//, '');
    }
    return trimmed;
  };

  const normalizeApiImagesToUrls = (imagesRaw) => {
    if (!Array.isArray(imagesRaw)) return [];
    return imagesRaw
      .map((img) => {
        if (!img) return '';
        if (typeof img === 'string') return normalizePhotoValue(img);
        if (typeof img === 'object' && typeof img.image === 'string') return normalizePhotoValue(img.image);
        return '';
      })
      .filter((u) => typeof u === 'string' && u.trim() !== '');
  };

  const getThumbnailFromImages = (imagesRaw) => {
    if (!Array.isArray(imagesRaw) || imagesRaw.length === 0) return '';
    const thumb = imagesRaw.find((x) => x && typeof x === 'object' && x.is_thumbnail && typeof x.image === 'string');
    if (thumb?.image) return normalizePhotoValue(thumb.image);
    const firstUrl = normalizeApiImagesToUrls(imagesRaw)[0];
    return firstUrl || '';
  };

  const uploadToCloudinary = async (dataUrlOrFile, filename) => {
    if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
      throw new Error('Cloudinary is not configured. Please add CLOUDINARY_CLOUD_NAME and CLOUDINARY_UPLOAD_PRESET in Products.js');
    }

    const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
    const formData = new FormData();
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('file', dataUrlOrFile);
    if (filename) {
      formData.append('filename_override', filename);
    }

    const res = await fetch(url, {
      method: 'POST',
      body: formData,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const msg = data?.error?.message || data?.message || 'Failed to upload image to Cloudinary';
      throw new Error(msg);
    }
    return data?.secure_url || data?.url || '';
  };

  const uploadToCloudinaryWithProgress = (dataUrlOrFile, filename, onProgress) => {
    if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
      return Promise.reject(new Error('Cloudinary is not configured. Please add CLOUDINARY_CLOUD_NAME and CLOUDINARY_UPLOAD_PRESET in Products.js'));
    }

    const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
    const formData = new FormData();
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('file', dataUrlOrFile);
    if (filename) {
      formData.append('filename_override', filename);
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
        if (typeof onProgress === 'function') onProgress(pct);
      };

      xhr.onload = () => {
        let data = null;
        try { data = JSON.parse(xhr.responseText); } catch (_) { data = null; }

        if (xhr.status >= 200 && xhr.status < 300) {
          const out = data?.secure_url || data?.url || '';
          resolve(out);
          return;
        }

        const msg = data?.error?.message || data?.message || `Upload failed (${xhr.status})`;
        reject(new Error(msg));
      };

      xhr.onerror = () => reject(new Error('Network error while uploading image'));
      xhr.onabort = () => reject(new Error('Upload aborted'));

      xhr.send(formData);
    });
  };

  // Future extension point for additional item normalization if backend shape changes
  const normalizeSubCategory = () => '';

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const syncTrendingIds = (nextProducts) => {
    try {
      const trendingIds = nextProducts
        .filter(p => p.segment === 'trending')
        .map(p => p.id)
        .filter(Boolean);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('trendingProductIds', JSON.stringify(trendingIds));
      }
    } catch (e) {
      console.warn('Failed to sync trendingProductIds to localStorage', e);
    }
  };

  const tabbedProducts = useMemo(() => {
    if (!Array.isArray(filteredProducts)) return [];
    if (filteredProducts.length === 0) return [];

    switch (activeTab) {
      case 'new': {
        // New Arrivals: always show the last 6 items added (highest IDs)
        const sortedByIdDesc = [...filteredProducts].sort((a, b) => (b.id || 0) - (a.id || 0));
        return sortedByIdDesc.slice(0, 6);
      }
      case 'trending':
        return filteredProducts.filter(p => p.segment === 'trending');
      case 'sale':
        return filteredProducts.filter(p => p.segment === 'sale');
      case 'all':
      default:
        return filteredProducts;
    }
  }, [filteredProducts, activeTab]);

  // Reset to first page whenever filters or tabs change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, filteredProducts.length]);

  const totalPages = Math.max(1, Math.ceil(tabbedProducts.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = tabbedProducts.slice(startIndex, endIndex);

  // Load products from API when component mounts
  const loadProducts = async (forceRefresh = false) => {
    try {
      setIsLoadingProducts(true);
      console.log('🔄 Loading products from API...', forceRefresh ? '(Force Refresh)' : '');
      
      // Add cache-busting parameter if force refresh is requested
      const response = await getAllItems();
      console.log('📦 Products loaded from API:', response);
      console.log('📦 Number of items received:', response.items?.length || 0);
      
      if (!response.items || !Array.isArray(response.items)) {
        throw new Error('Invalid response format from API');
      }
      
      // Map API response to our product format
      const mappedProducts = response.items.map(item => {
        const urls = normalizeApiImagesToUrls(item.images);
        const thumbUrl = getThumbnailFromImages(item.images);
        const ordered = thumbUrl ? [thumbUrl, ...urls.filter((u) => u !== thumbUrl)] : urls;

        return {
          id: item.id,
          name: item.name,
          description: item.description,
          category: item.category,
          price: parseFloat(item.price),
          stock: typeof item.amount === 'number' ? item.amount : 0,
          status: 'In Stock',
          rating: item.average_rating || 0,
          images: ordered,
          owner: item.owner,
          segment: 'all'
        };
      });
      
      console.log('✅ Mapped products:', mappedProducts);
      const sorted = sortByNewestFirst(mappedProducts);
      setProducts(sorted);
      syncTrendingIds(sorted);
      
      if (forceRefresh) {
        toast.success(`🔄 Force refreshed ${mappedProducts.length} products`, {
          position: "top-right",
          autoClose: 2000,
          style: {
            background: '#3B82F6',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500'
          }
        });
      } else {
        toast.success(`✅ Loaded ${mappedProducts.length} products`, {
          position: "top-right",
          autoClose: 2000,
          style: {
            background: '#065F46',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500'
          }
        });
      }
    } catch (error) {
      console.error('❌ Error loading products:', error);
      toast.error(`❌ ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Initialize from cached items on first mount; fallback to API if cache empty
  useEffect(() => {
    if (Array.isArray(cachedItems) && cachedItems.length > 0) {
      // Map cached items to local product shape quickly and normalize photos
      const mapped = cachedItems.map(item => {
        const urls = normalizeApiImagesToUrls(item.images);
        const thumbUrl = getThumbnailFromImages(item.images);
        const images = thumbUrl ? [thumbUrl, ...urls.filter((u) => u !== thumbUrl)] : urls;

        return {
          id: item.id,
          name: item.name,
          description: item.description,
          category: item.category,
          price: parseFloat(item.price),
          stock: typeof item.amount === 'number' ? item.amount : 0,
          status: 'In Stock',
          rating: item.average_rating || 0,
          images,
          owner: item.owner,
          segment: 'all',
        };
      });
      const sorted = sortByNewestFirst(mapped);
      setProducts(sorted);
      syncTrendingIds(sorted);
    } else {
      // Fallback to API only if no cache
      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // If user types a URL into photo field, clear any uploaded files (mutually exclusive)
    if (name === 'photo') {
      setUploadedFiles([]);
      setThumbnailIndex(0);
    }
    setNewProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const readers = files.map(file => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ name: file.name, dataUrl: reader.result });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }));
    try {
      const results = await Promise.all(readers);
      // Only clear URL input if we don't already have URLs (mutually exclusive)
      if (results.length > 0 && !newProduct.photo) {
        setNewProduct(prev => ({ ...prev, photo: '' }));
      }
      // Append new files to existing uploaded files
      setUploadedFiles(prev => [...prev, ...results]);
      // Default thumbnail to the first new image if no existing images
      setThumbnailIndex((prev) => {
        if (prev === 0 && uploadedFiles.length === 0) return 0;
        return prev;
      });
    } catch (err) {
      toast.error('❌ Failed to read image. Please try a different file.');
    }
  };

  const removeImage = (index) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
    setThumbnailIndex((prev) => {
      if (newFiles.length === 0) return 0;
      if (prev === index) return 0;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  // Remove a photo from the photo URL field (already uploaded)
  const removePhotoUrl = (index) => {
    const currentUrls = (newProduct.photo || '').split(',').map(u => u.trim()).filter(Boolean);
    currentUrls.splice(index, 1);
    setNewProduct((prev) => ({
      ...prev,
      photo: currentUrls.join(', '),
    }));
    // Adjust thumbnail index if needed
    setThumbnailIndex((prev) => {
      if (currentUrls.length === 0) return 0;
      if (prev === index) return 0;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const handleUploadSelectedImages = async () => {
    if (uploadedFiles.length === 0) return;

    setIsUploadingImages(true);
    setUploadProgress(0);
    try {
      // Upload ALL images to Cloudinary, not just the first one
      const uploadedUrls = [];
      const totalFiles = uploadedFiles.length;
      
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const progress = Math.round(((i + 1) / totalFiles) * 100);
        setUploadProgress(progress);
        
        const url = await uploadToCloudinaryWithProgress(file.dataUrl, file.name, (pct) => {
          // Update progress based on this file's upload
          const fileProgress = pct / totalFiles;
          const baseProgress = (i / totalFiles) * 100;
          setUploadProgress(Math.round(baseProgress + fileProgress));
        });
        
        if (url) {
          uploadedUrls.push(url);
        }
      }

      if (uploadedUrls.length === 0) {
        throw new Error('No images were uploaded successfully');
      }

      // Get existing URLs from photo field
      const existingPhotoUrls = (newProduct.photo || '').split(',').map(u => u.trim()).filter(Boolean);
      
      // Append new URLs to existing ones
      const allUrls = [...existingPhotoUrls, ...uploadedUrls];
      
      // Set all uploaded URLs as comma-separated in the photo field
      setNewProduct((prev) => ({
        ...prev,
        photo: allUrls.join(', '),
      }));
      setUploadedFiles([]);
      
      // If this is the first upload, set thumbnail to first image
      if (existingPhotoUrls.length === 0) {
        setThumbnailIndex(0);
      } else {
        // Keep the current thumbnail index (relative to all images)
        setThumbnailIndex(existingPhotoUrls.length);
      }

      toast.success(`✅ Uploaded ${uploadedUrls.length} image(s) to Cloudinary. Total images: ${allUrls.length}`, {
        position: "top-right",
        autoClose: 2500,
        style: {
          background: 'linear-gradient(135deg, #065F46 0%, #047857 100%)',
          color: 'white',
          fontSize: '14px',
          fontWeight: '700',
          borderRadius: '12px',
        }
      });
    } catch (err) {
      toast.error(`❌ ${err?.message || 'Failed to upload images'}`, {
        position: "top-right",
        autoClose: 4000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '600'
        }
      });
    } finally {
      setIsUploadingImages(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is admin
    if (!isAdmin()) {
      toast.error('🚫 Access denied. Admin privileges required.', {
        position: "top-right",
        autoClose: 4000,
        style: {
          background: '#F59E0B',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
      return;
    }

    // Validate required fields
    const requiredName = String(newProduct?.name ?? '').trim();
    const requiredDescription = String(newProduct?.description ?? '').trim();
    const requiredCategory = String(newProduct?.category ?? '').trim();
    const priceMissing = newProduct?.price === undefined || newProduct?.price === null || String(newProduct?.price).trim() === '';

    if (!requiredName || !requiredDescription || !requiredCategory) {
      toast.error('⚠️ Please fill in all required fields.', {
        position: "top-right",
        autoClose: 4000,
        style: {
          background: '#F59E0B',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // If files are selected, require explicit upload first (so we can show progress and avoid duplicate uploads)
      if (uploadedFiles.length > 0) {
        toast.error('⚠️ Please click "Upload to Cloudinary" first, then save the product.', {
          position: "top-right",
          autoClose: 4000,
          style: {
            background: '#F59E0B',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500'
          }
        });
        setIsSubmitting(false);
        return;
      }

      // Prefer locally uploaded file as primary photo; include filename (mutually exclusive with URL)
      const amountNum = newProduct.amount === '' || newProduct.amount === null || typeof newProduct.amount === 'undefined'
        ? null
        : Number(newProduct.amount);

      const urlFromField = (newProduct.photo || '').trim();
      // Support comma-separated URLs for multiple images
      let finalUrls = urlFromField ? urlFromField.split(',').map(u => u.trim()).filter(Boolean) : [];

      // On edit: if no new images were provided, preserve existing ones
      if (isEditing && finalUrls.length === 0) {
        const existing = products.find((p) => String(p.id) === String(newProduct.id));
        const existingUrls = Array.isArray(existing?.images) ? existing.images.filter(Boolean) : [];
        finalUrls = existingUrls;
      }

      // Use first image as thumbnail by default (can be changed by thumbnailIndex)
      const effectiveThumbIndex = Math.min(thumbnailIndex, Math.max(0, finalUrls.length - 1));

      const originalUrls = Array.isArray(newProduct.originalImages)
        ? newProduct.originalImages.filter(Boolean)
        : [];
      const normalizedFinalUrls = finalUrls.filter(Boolean);
      
      // Find the original thumbnail index
      const originalThumbIndex = newProduct.originalImages?.findIndex(img => {
        if (typeof img === 'string') return false;
        return img?.is_thumbnail === true;
      }) ?? 0;
      
      const imagesHaveChanged = !isEditing
        ? true
        : (
          // Any new upload implies a change
          uploadedFiles.length > 0 ||
          // URL list changed (order or values)
          normalizedFinalUrls.length !== originalUrls.length ||
          normalizedFinalUrls.some((u, idx) => String(u) !== String(originalUrls[idx])) ||
          // Thumbnail choice changed
          effectiveThumbIndex !== originalThumbIndex
        );

      const imagesPayload = normalizedFinalUrls.map((u, idx) => ({ image: u, is_thumbnail: idx === effectiveThumbIndex }));

      const baseItemData = {
        name: requiredName,
        description: requiredDescription,
        price: newProduct.price,
        category: requiredCategory,
        amount: Number.isFinite(amountNum) ? amountNum : null,
        ...(imagesHaveChanged ? { images: imagesPayload } : {}),
      };

      const itemData = baseItemData;

      let response;
      if (isEditing) {
        // Edit existing item
        response = await editItem(newProduct.id, itemData, token);

        // editItem now returns the item directly (or unwraps { item })
        const updated = response;
        const updatedItemImages = Array.isArray(updated?.images) ? updated.images : [];
        const urls = normalizeApiImagesToUrls(updatedItemImages);
        const thumbUrl = getThumbnailFromImages(updatedItemImages);
        const ordered = thumbUrl ? [thumbUrl, ...urls.filter((u) => u !== thumbUrl)] : urls;

        const updatedItem = {
          id: updated.id,
          name: updated.name,
          description: updated.description,
          category: updated.category,
          price: parseFloat(updated.price),
          stock: typeof updated.amount === 'number' ? updated.amount : 0,
          status: 'In Stock',
          rating: updated.average_rating || 0,
          images: ordered,
          segment: newProduct.segment || 'all',
        };

        // Update the local state immediately with the API response
        const updatedList = sortByNewestFirst(products.map(p => p.id === newProduct.id ? updatedItem : p));
        setProducts(updatedList);
        syncTrendingIds(updatedList);
        
        toast.success(`✅ Updated: ${updated.name}`, {
          position: "top-right",
          autoClose: 3500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: 'linear-gradient(135deg, #065F46 0%, #047857 100%)',
            color: 'white',
            fontSize: '15px',
            fontWeight: '700',
            borderRadius: '12px',
            boxShadow: '0 12px 28px rgba(0,0,0,0.18)'
          }
        });
        
        // Optional verification: compare returned values to what was sent
        const dataMatches = (
          String(updated?.name || '') === String(itemData.name || '') &&
          String(updated?.description || '') === String(itemData.description || '') &&
          String(updated?.category || '') === String(itemData.category || '')
        );

        if (!dataMatches) {
          toast.warning('⚠️ Edit saved. Please refresh to verify updates.', {
            position: "top-right",
            autoClose: 7000,
            style: {
              background: '#F59E0B',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500'
            }
          });
        }
        // Push admin notification for successful update
        addNotification({
          title: 'Product updated',
          message: `"${updated.name}" was updated successfully.`,
          type: 'product_updated',
        });
      } else {
        // Add new item
        response = await addItem(itemData, token);

        // addItem returns the item directly
        const created = response;
        const createdImages = Array.isArray(created?.images) ? created.images : [];
        const urls = normalizeApiImagesToUrls(createdImages);
        const thumbUrl = getThumbnailFromImages(createdImages);
        const ordered = thumbUrl ? [thumbUrl, ...urls.filter((u) => u !== thumbUrl)] : urls;

        const newItem = {
          id: created.id,
          name: created.name,
          description: created.description,
          category: created.category,
          price: parseFloat(created.price),
          stock: typeof created.amount === 'number' ? created.amount : 0,
          status: 'In Stock',
          rating: created.average_rating || 0,
          images: ordered,
          segment: ['new', 'trending', 'sale'].includes(activeTab) ? activeTab : 'all',
        };

        // Add the new item to local state immediately
        const updatedList = sortByNewestFirst([newItem, ...products]);
        setProducts(updatedList);
        syncTrendingIds(updatedList);

        // Push admin notification
        addNotification({
          title: 'Product created',
          message: `"${created.name}" was added successfully.`,
          type: 'product_created',
        });
        
        toast.success(`✅ Added: ${created.name}`, {
          position: "top-right",
          autoClose: 3500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)',
            color: 'white',
            fontSize: '15px',
            fontWeight: '700',
            borderRadius: '12px',
            boxShadow: '0 12px 28px rgba(0,0,0,0.18)'
          }
        });
        
        // Don't refresh immediately - let the user see the changes
        // Only refresh if there's a specific need to sync with server
      }

      // Reset form
      setNewProduct({
        name: '',
        description: '',
        category: '',
        price: '',
        photo: '',
        amount: '',
        originalImages: [],
        originalFields: {
          name: '',
          description: '',
          category: '',
          price: '',
          amount: '',
        },
        segment: 'all'
      });
      setUploadedFiles([]);
      setThumbnailIndex(0);
      setIsModalOpen(false);
      setIsEditing(false);
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error(`❌ ${error.message || 'Failed to add product. Please try again.'}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    // Join all images with commas for the photo field
    const imagesFromList = product.images && product.images.length > 0 ? product.images.filter(Boolean) : [];
    const photoFromList = imagesFromList.join(', ');
    const normalizedPhoto = normalizePhotoValue(photoFromList);

    // Find the original thumbnail index
    const originalThumbIndex = imagesFromList.findIndex((img, idx) => {
      // Check if it's the first image (default) or has is_thumbnail flag
      return idx === 0 || (typeof img === 'object' && img?.is_thumbnail === true);
    });

    setNewProduct({
      id: product.id,
      name: product.name,
      description: product.description || '',
      category: product.category,
      price: product.price,
      photo: normalizedPhoto,
      amount: typeof product.stock === 'number' ? product.stock : '',
      originalImages: imagesFromList,
      originalFields: {
        name: product.name,
        description: product.description || '',
        category: product.category,
        price: product.price,
        amount: typeof product.stock === 'number' ? product.stock : '',
      },
      // Carry over the existing segment so edits do not move the product between tabs
      segment: product.segment || 'all'
    });
    setUploadedFiles([]);
    setThumbnailIndex(originalThumbIndex >= 0 ? originalThumbIndex : 0);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const confirmDelete = (product) => {
    setSelectedProduct(product);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    const toDelete = selectedProduct;
    if (!toDelete?.id) {
      toast.error('❌ No product selected to delete.');
      return;
    }

    // Check if user is admin
    if (!isAdmin()) {
      toast.error('🚫 Access denied. Admin privileges required.', {
        position: "top-right",
        autoClose: 4000,
        style: {
          background: '#F59E0B',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      await deleteItem(toDelete.id, token);

      const updatedList = sortByNewestFirst(products.filter(p => p.id !== toDelete.id));
      setProducts(updatedList);
      syncTrendingIds(updatedList);
      setIsDeleteModalOpen(false);

      toast.success('🗑️ Product deleted successfully!', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: '#065F46',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });

      addNotification({
        title: 'Product deleted',
        message: `"${toDelete.name}" was removed from the catalog.`,
        type: 'product_deleted',
      });
    } catch (error) {
      console.error('❌ Error deleting product:', error);
      toast.error(`❌ ${error.message || 'Failed to delete product. Please try again.'}`, {
        position: "top-right",
        autoClose: 5000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    }
  };

  const handleViewAllItems = async () => {
    try {
      setIsLoadingAllItems(true);
      const response = await getAllItems();
      console.log('All items response:', response);
      
      // Map API response to our product format
      const mappedItems = response.items.map(item => {
        const urls = normalizeApiImagesToUrls(item.images);
        const thumbUrl = getThumbnailFromImages(item.images);
        const ordered = thumbUrl ? [thumbUrl, ...urls.filter((u) => u !== thumbUrl)] : urls;

        return {
          id: item.id,
          name: item.name,
          description: item.description,
          category: item.category,
          price: parseFloat(item.price),
          stock: typeof item.amount === 'number' ? item.amount : 0,
          status: 'In Stock',
          rating: item.average_rating || 0,
          images: ordered,
          owner: item.owner
        };
      });
      
      setAllItems(mappedItems);
      setIsViewAllModalOpen(true);
    } catch (error) {
      console.error('Error fetching all items:', error);
      toast.error(`❌ ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    } finally {
      setIsLoadingAllItems(false);
    }
  };

  const exportToPDF = () => {
    try {
      // Use allItems if available, otherwise use products
      const itemsToExport = allItems.length > 0 ? allItems : products;
      
      if (itemsToExport.length === 0) {
        toast.error('❌ No items to export', {
          position: "top-right",
          autoClose: 3000,
          style: {
            background: '#EF4444',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500'
          }
        });
        return;
      }

      const doc = new jsPDF('p', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add title
      // Logo (if available)
      try {
        const logoImg = new Image();
        logoImg.src = '/logo.png';
        // Note: In browsers, addImage from URL can be restricted; fallback silently
        // We'll draw a small placeholder rect if addImage fails
        doc.addImage(logoImg, 'PNG', 40, 32, 40, 40);
      } catch (e) {
        // Draw placeholder box
        doc.setDrawColor(230);
        doc.rect(40, 32, 40, 40);
      }
      doc.setFontSize(18);
      doc.text('Brand Decor - All Products', 90, 50);
      
      // Add date
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 90, 64);
      
      // Prepare table data (no sub category column)
      const tableData = itemsToExport.map(item => [
        item.id,
        item.name,
        item.category,
        `KSh ${item.price.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        item.owner ? `${item.owner.first_name} ${item.owner.last_name}` : 'N/A',
        (item.rating || 0).toFixed(1)
      ]);
      
      // Add table
      autoTable(doc, {
        head: [['ID', 'Product Name', 'Category', 'Price', 'Owner', 'Rating']],
        body: tableData,
        startY: 90,
        styles: {
          fontSize: 9,
          cellPadding: 6
        },
        headStyles: {
          fillColor: [249, 115, 22], // Orange brand
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251] // Light gray
        }
      });
      
      // Footer
      const addFooter = () => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text('Brand Decor Furniture • branddecor.ke', 40, doc.internal.pageSize.getHeight() - 30);
          doc.text(`Page ${i} of ${pageCount}`, pageWidth - 100, doc.internal.pageSize.getHeight() - 30);
        }
      };
      addFooter();
      
      // Save the PDF
      doc.save('brand-decor-products.pdf');
      
      toast.success('📄 PDF exported successfully!', {
        position: "top-right",
        autoClose: 3000,
        style: {
          background: '#065F46',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('❌ Failed to export PDF', {
        position: "top-right",
        autoClose: 3000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    }
  };

  // Test function to verify edit functionality
  const testEditFunctionality = async () => {
    if (products.length === 0) {
      toast.error('❌ No products available to test', {
        position: "top-right",
        autoClose: 3000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
      return;
    }

    const testProduct = products[0];
    const originalName = testProduct.name;
    const testName = `${originalName} - TEST ${Date.now()}`;
    
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🧪 Testing edit functionality...');
      console.log('🧪 Original product:', testProduct);
      
      const testData = {
        name: testName,
        description: testProduct.description,
        price: testProduct.price,
        category: testProduct.category,
        photo: testProduct.images && testProduct.images.length > 0 ? testProduct.images[0] : ""
      };

      console.log('🧪 Test data to send:', testData);
      
      const response = await editItem(testProduct.id, testData, token);
      console.log('🧪 Edit response:', response);
      
      // Wait a moment then refresh to see if the change persisted
      setTimeout(async () => {
        console.log('🧪 Refreshing to check if change persisted...');
        await loadProducts(true);
        
        const updatedProduct = products.find(p => p.id === testProduct.id);
        if (updatedProduct && updatedProduct.name === testName) {
          console.log('✅ Test successful - edit persisted');
          toast.success('✅ Edit test successful - changes persisted!', {
            position: "top-right",
            autoClose: 5000,
            style: {
              background: '#065F46',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500'
            }
          });
        } else {
          console.log('❌ Test failed - edit did not persist');
          toast.error('❌ Edit test failed - changes did not persist', {
            position: "top-right",
            autoClose: 5000,
            style: {
              background: '#EF4444',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500'
            }
          });
        }
      }, 2000);
      
    } catch (error) {
      console.error('❌ Test edit failed:', error);
      toast.error(`❌ Test edit failed: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    }
  };

  // Handle viewing item details
  const handleViewItemDetails = async (product) => {
    try {
      setIsLoadingItemDetails(true);
      console.log('🔍 Fetching details for item:', product.id);

      // getItemDetails already returns the item object (not { item: ... })
      const item = await getItemDetails(product.id);
      console.log('📦 Item details item:', item);

      // New backend uses images[] with { image, is_thumbnail }
      const detailsThumb = getThumbnailFromImages(item?.images);
      const fallbackListPhoto = Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : '';
      const detailsPhoto = normalizePhotoValue(detailsThumb) || fallbackListPhoto;

      const detailsImagesUrls = normalizeApiImagesToUrls(item?.images);
      const orderedDetailsImages = detailsPhoto
        ? [detailsPhoto, ...detailsImagesUrls.filter((u) => u !== detailsPhoto)]
        : detailsImagesUrls;

      const mergedDetails = {
        ...product,        // keep list data (name, category, images, rating, etc.) as a base
        ...item,           // override with authoritative item fields from backend
        photo: detailsPhoto,
        images: orderedDetailsImages.length > 0 ? orderedDetailsImages : (Array.isArray(product.images) ? product.images : []),
        average_rating: typeof item?.average_rating === 'number'
          ? item.average_rating
          : (typeof product?.rating === 'number' ? product.rating : 0),
      };

      setItemDetails(mergedDetails);
      setIsItemDetailModalOpen(true);

    } catch (error) {
      console.error('❌ Error fetching item details:', error);
      toast.error(`❌ ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        style: {
          background: '#EF4444',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500'
        }
      });
    } finally {
      setIsLoadingItemDetails(false);
    }
  };

  // Test image loading functionality
  const testImageLoading = () => {
    console.log('🧪 Testing image loading...');
    console.log('📊 Current products:', products);
    
    products.forEach((product, index) => {
      console.log(`📸 Product ${index + 1} (ID: ${product.id}):`, {
        name: product.name,
        images: product.images,
        hasImages: !!product.images,
        imagesLength: product.images?.length || 0,
        firstImage: product.images?.[0]
      });
    });
    
    toast.info('🧪 Image loading test completed. Check console for details.', {
      position: "top-right",
      autoClose: 3000,
      style: {
        background: '#3B82F6',
        color: 'white',
        fontSize: '14px',
        fontWeight: '500'
      }
    });
  };

  const openImageModal = (product, index = 0) => {
    setSelectedProduct(product);
    setCurrentImageIndex(index);
    setIsImageModalOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex(prev => 
      prev === selectedProduct.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex(prev => 
      prev === 0 ? selectedProduct.images.length - 1 : prev - 1
    );
  };

  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-400" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<FaStar key={i} className="text-yellow-400" />);
      } else {
        stars.push(<FaRegStar key={i} className="text-gray-300" />);
      }
    }
    
    return (
      <div className="flex items-center">
        {stars}
        <span className="ml-1 text-sm text-gray-500">({rating})</span>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-6 flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div className="w-full max-w-md">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <FaSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-between lg:justify-end">
          <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs sm:text-sm">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={
                'px-3 py-1 rounded-full font-medium transition-colors ' +
                (activeTab === 'all'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-white')
              }
            >
              All Items
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('new')}
              className={
                'px-3 py-1 rounded-full font-medium transition-colors ' +
                (activeTab === 'new'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-white')
              }
            >
              New Arrivals
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('trending')}
              className={
                'px-3 py-1 rounded-full font-medium transition-colors ' +
                (activeTab === 'trending'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-white')
              }
            >
              Trending
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('sale')}
              className={
                'px-3 py-1 rounded-full font-medium transition-colors ' +
                (activeTab === 'sale'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-white')
              }
            >
              On Sale
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setNewProduct({
                name: '',
                description: '',
                category: '',
                price: '',
                photo: '',
                amount: '',
                // When creating from a specific tab, default the segment to that tab
                segment: ['new', 'trending', 'sale'].includes(activeTab) ? activeTab : 'all'
              });
              setUploadedFiles([]);
              setThumbnailIndex(0);
              setIsEditing(false);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <FaPlus className="mr-2 h-4 w-4" />
            Add Product
          </button>
        </div>
      </div>

      {!isModalOpen && isItemDetailModalOpen && (
        <div className="rounded-xl bg-white shadow border border-gray-100">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Product Details</h2>
              <p className="text-sm text-gray-500 mt-1">View full product information</p>
            </div>
            <button
              type="button"
              onClick={() => setIsItemDetailModalOpen(false)}
              className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
            >
              Back to list
            </button>
          </div>

          <div className="p-6">
            {isLoadingItemDetails ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading product details...</span>
              </div>
            ) : itemDetails ? (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <img
                      src={itemDetails.photo || FALLBACK_IMG_192}
                      alt={itemDetails.name}
                      className="w-full h-80 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_IMG_192;
                      }}
                    />
                  </div>

                  {Array.isArray(itemDetails.images) && itemDetails.images.length > 1 && (
                    <div className="mt-4 grid grid-cols-5 gap-2">
                      {itemDetails.images.slice(0, 10).map((imgUrl, idx) => (
                        <button
                          type="button"
                          key={imgUrl + idx}
                          onClick={() => setItemDetails((prev) => ({ ...prev, photo: imgUrl }))}
                          className={
                            'rounded-lg border p-1 bg-white hover:bg-gray-50 ' +
                            ((itemDetails.photo || '') === imgUrl ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200')
                          }
                        >
                          <img
                            src={imgUrl}
                            alt={`${itemDetails.name} ${idx + 1}`}
                            className="h-14 w-full object-cover rounded-md"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = FALLBACK_IMG_48;
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="lg:col-span-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{itemDetails.name}</h3>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                          {itemDetails.category}
                        </span>
                        <span className="text-xs text-gray-500">ID: #{itemDetails.id}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Price</div>
                      <div className="text-2xl font-extrabold text-gray-900">
                        KSh {Number(itemDetails.price || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Stock</div>
                      <div className="mt-1 text-lg font-bold text-gray-900">{typeof itemDetails.amount === 'number' ? itemDetails.amount : (itemDetails.stock || 0)} units</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Rating</div>
                      <div className="mt-1">{renderRating(Number(itemDetails.average_rating || itemDetails.rating || 0))}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Owner</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">
                        {itemDetails.owner ? `${itemDetails.owner.first_name} ${itemDetails.owner.last_name}` : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">{itemDetails.owner?.email || ''}</div>
                    </div>
                  </div>

                  <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</div>
                    <p className="mt-2 text-sm leading-relaxed text-gray-800">{itemDetails.description}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-10 text-center text-gray-600">No product details available.</div>
            )}
          </div>
        </div>
      )}

      {!isModalOpen && !isItemDetailModalOpen && (
        <div className="rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <div className="flex items-center space-x-2">
                      <span>Product</span>
                      <FaFilter className="h-3 w-3 text-gray-400" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Price</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Stock</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoadingProducts ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                        <span className="text-gray-600">Loading products...</span>
                      </div>
                    </td>
                  </tr>
                ) : tabbedProducts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <FaImage className="h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                        <p className="text-gray-500 mb-4">
                          {products.length === 0
                            ? 'Get started by adding your first product.'
                            : 'No products match your search criteria.'}
                        </p>
                        {products.length === 0 && (
                          <button
                            onClick={() => setIsModalOpen(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <FaPlus className="h-4 w-4 mr-2" />
                            Add Your First Product
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-12 w-12 flex-shrink-0 relative">
                            {(() => {
                              const validImages = product.images?.filter(img => img && img.trim() !== '' && img !== 'null' && img !== 'undefined') || [];
                              const hasValidImage = validImages.length > 0;

                              if (hasValidImage) {
                                return (
                                  <>
                                    <img
                                      className="h-12 w-12 rounded-md object-cover border border-gray-200"
                                      src={validImages[0]}
                                      alt={product.name}
                                      onError={(e) => {
                                        console.log('❌ Image failed to load:', validImages[0]);
                                        e.target.onerror = null;
                                        e.target.src = FALLBACK_IMG_48;
                                      }}
                                      onLoad={() => {
                                        console.log('✅ Image loaded successfully:', validImages[0]);
                                      }}
                                    />
                                    {validImages.length > 1 && (
                                      <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                        {validImages.length}
                                      </div>
                                    )}
                                  </>
                                );
                              }

                              return (
                                <img
                                  className="h-12 w-12 rounded-md object-cover border border-gray-200"
                                  src={FALLBACK_IMG_48}
                                  alt={product.name}
                                />
                              );
                            })()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            <div className="text-xs text-gray-500">ID: #{product.id}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{product.category}</div>
                            {/* Show all images in a small gallery on hover */}
                            {product.images?.length > 1 && (
                              <div className="flex gap-1 mt-1">
                                {product.images.slice(0, 5).map((img, idx) => (
                                  <img
                                    key={idx}
                                    src={img}
                                    alt={`${product.name} ${idx + 1}`}
                                    className="h-6 w-6 rounded object-cover border border-gray-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ))}
                                {product.images.length > 5 && (
                                  <span className="text-xs text-gray-500 self-center">+{product.images.length - 5}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                          {product.category}
                        </span>
                      </td>

                      {/* Sub category column removed to match backend shape */}
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        KSh {product.price.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{product.stock} units</td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={
                            'inline-flex rounded-full px-2 text-xs font-semibold leading-5 ' +
                            (product.status === 'In Stock'
                              ? 'bg-green-100 text-green-800'
                              : product.status === 'Low Stock'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800')
                          }
                        >
                          {product.status}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewItemDetails(product)}
                          className="mr-3 text-green-600 hover:text-green-900"
                          title="View Details"
                        >
                          <FaInfoCircle className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(product)}
                          className="mr-3 text-blue-600 hover:text-blue-900"
                          title="Edit Product"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => confirmDelete(product)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Product"
                        >
                          <FaTrashAlt className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
            {/* Mobile pagination */}
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safeCurrentPage === 1}
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 self-center">
                Page {safeCurrentPage} of {totalPages}
              </span>
              <button
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safeCurrentPage === totalPages}
              >
                Next
              </button>
            </div>

            {/* Desktop summary + pagination */}
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing
                  <span className="font-medium"> {tabbedProducts.length === 0 ? 0 : startIndex + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">{startIndex + paginatedProducts.length}</span>
                  {' '}of{' '}
                  <span className="font-medium">{tabbedProducts.length}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    className="relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safeCurrentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const page = index + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={
                          'relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0 ' +
                          (page === safeCurrentPage
                            ? 'z-10 bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50')
                        }
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    className="relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safeCurrentPage === totalPages}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Product Inline Panel */}
      {isModalOpen && (
        <div className="bg-white rounded-lg shadow border mt-4">
          <form onSubmit={handleSubmit}>
            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {isEditing ? 'Edit Product' : 'Add New Product'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {isEditing
                      ? 'Update the product details below, then save your changes.'
                      : 'Fill in the details to add a new product to the catalog.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FaTimes className="h-4 w-4 mr-1" />
                  Back to list
                </button>
              </div>

              <div className="space-y-4">
                    {/* Product Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={newProduct.name}
                        onChange={handleInputChange}
                        className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3"
                        placeholder="Enter product name"
                        required
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description *
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={newProduct.description}
                        onChange={handleInputChange}
                        rows={3}
                        className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3 min-h-[120px]"
                        placeholder="Enter product description"
                        required
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                        Category *
                      </label>
                      <input
                        type="text"
                        id="category"
                        name="category"
                        value={newProduct.category}
                        onChange={handleInputChange}
                        className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3"
                        placeholder="e.g. Living Room"
                        required
                      />
                    </div>

                    {/* Price */}
                    <div>
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                        Price (KSh)
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">KSh</span>
                        </div>
                        <input
                          type="number"
                          id="price"
                          name="price"
                          value={newProduct.price}
                          onChange={handleInputChange}
                          step="0.01"
                          min="0"
                          className="block w-full pl-14 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    {/* Amount */}
                    <div>
                      <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                        Amount
                      </label>
                      <input
                        type="number"
                        id="amount"
                        name="amount"
                        value={newProduct.amount}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3"
                        placeholder="e.g. 30"
                      />
                    </div>

                    {/* Photo URL */}
                    <div>
                      <label htmlFor="photo" className="block text-sm font-medium text-gray-700">
                        Photo URLs (comma-separated for multiple images)
                      </label>
                      <textarea
                        id="photo"
                        name="photo"
                        value={newProduct.photo}
                        onChange={handleInputChange}
                        rows={2}
                        className="mt-2 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3"
                        placeholder="Enter image URL (optional). For multiple images, separate with commas."
                      />
                      
                      {/* Photo Previews */}
                      {newProduct.photo && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Photo Previews ({newProduct.photo.split(',').filter(Boolean).length})
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {newProduct.photo.split(',').map((url, idx) => {
                              const trimmedUrl = url.trim();
                              if (!trimmedUrl) return null;
                              const allUrls = newProduct.photo.split(',').map(u => u.trim()).filter(Boolean);
                              return (
                                <div key={idx} className="relative inline-block group">
                                  <img
                                    src={trimmedUrl}
                                    alt={`Product ${idx + 1}`}
                                    className="h-32 w-32 object-cover rounded-lg border border-gray-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div 
                                    className="h-32 w-32 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center text-gray-500 text-sm hidden"
                                  >
                                    Invalid URL
                                  </div>
                                  <div className="absolute bottom-1 left-1 bg-blue-600 text-white text-xs rounded px-1">
                                    {idx === 0 ? 'Primary' : `#${idx + 1}`}
                                  </div>
                                  {/* Remove button */}
                                  <button
                                    type="button"
                                    onClick={() => removePhotoUrl(idx)}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Remove image"
                                  >
                                    <FaTimes size={12} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Local Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Upload Images</label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="mt-2 block w-full text-sm text-gray-900 file:mr-4 file:py-3 file:px-5 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />

                      {uploadedFiles.length > 0 && (
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="text-sm text-gray-600">
                            {uploadedFiles.length} file(s) selected
                          </div>
                          <button
                            type="button"
                            onClick={handleUploadSelectedImages}
                            disabled={isUploadingImages || uploadedFiles.length === 0}
                            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <FaUpload className="mr-2" />
                            {isUploadingImages ? 'Uploading...' : 'Upload to Cloudinary'}
                          </button>
                        </div>
                      )}

                      {isUploadingImages && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                            <span className="text-sm text-gray-700 font-medium">
                              Uploading {uploadProgress}%...
                            </span>
                          </div>
                          <div className="mt-3">
                            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                              <div
                                className="bg-blue-600 h-2.5 rounded-full transition-[width] duration-200"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Uploaded Files Preview */}
                      {uploadedFiles.length > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              New Images to Upload ({uploadedFiles.length})
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setUploadedFiles([]);
                                setThumbnailIndex(0);
                              }}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Clear All
                            </button>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {uploadedFiles.map((file, idx) => (
                              <div key={idx} className="relative rounded-md border bg-white p-2">
                                <img
                                  src={file.dataUrl}
                                  alt={file.name || `Upload ${idx + 1}`}
                                  className="h-24 w-full object-cover rounded-md"
                                />

                                <div className="mt-2 flex items-center justify-between">
                                  <label className="inline-flex items-center text-xs text-gray-700">
                                    <input
                                      type="radio"
                                      name="thumbnail"
                                      checked={thumbnailIndex === idx}
                                      onChange={() => setThumbnailIndex(idx)}
                                      className="mr-2"
                                    />
                                    Thumbnail
                                  </label>

                                  <button
                                    type="button"
                                    onClick={() => removeImage(idx)}
                                    className="inline-flex items-center justify-center rounded-full bg-red-500 text-white h-7 w-7"
                                    title="Remove image"
                                  >
                                    <FaTimes size={12} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add More Images Button */}
                      {newProduct.photo && (
                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              // Click the hidden file input
                              document.getElementById('additional-image-upload')?.click();
                            }}
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                          >
                            <FaPlus className="mr-1" />
                            Add More Images
                          </button>
                          <input
                            id="additional-image-upload"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </div>
                      )}
                    </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Product' : 'Add Product')}
              </button>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsDeleteModalOpen(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FaTrashAlt className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Product
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete "{selectedProduct?.name}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View All Items Modal */}
      {isViewAllModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsViewAllModalOpen(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    All Items ({allItems.length})
                  </h3>
                  <button
                    onClick={() => setIsViewAllModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="h-6 w-6" />
                  </button>
                </div>

                {isLoadingAllItems ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading all items...</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allItems.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-10 w-10 flex-shrink-0 relative">
                                  {(() => {
                                    const validImages = item.images?.filter(img => img && img.trim() !== '' && img !== 'null' && img !== 'undefined') || [];
                                    const hasValidImage = validImages.length > 0;
                                    
                                    if (hasValidImage) {
                                      return (
                                        <>
                                          <img 
                                            className="h-10 w-10 rounded-md object-cover border border-gray-200" 
                                            src={validImages[0]} 
                                            alt={item.name}
                                            onError={(e) => {
                                              console.log('❌ Image failed to load in all items:', validImages[0]);
                                              e.target.onerror = null;
                                              e.target.src = FALLBACK_IMG_40;
                                            }}
                                            onLoad={() => {
                                              console.log('✅ Image loaded successfully in all items:', validImages[0]);
                                            }}
                                          />
                                          {validImages.length > 1 && (
                                            <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                              {validImages.length}
                                            </div>
                                          )}
                                        </>
                                      );
                                    } else {
                                      return (
                                        <img 
                                          className="h-10 w-10 rounded-md object-cover border border-gray-200" 
                                          src={FALLBACK_IMG_40} 
                                          alt={item.name}
                                        />
                                      );
                                    }
                                  })()}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                  <div className="text-xs text-gray-500">ID: #{item.id}</div>
                                  {item.images?.length > 1 && (
                                    <div className="flex gap-0.5 mt-1">
                                      {item.images.slice(0, 4).map((img, idx) => (
                                        <img
                                          key={idx}
                                          src={img}
                                          alt={`${item.name} ${idx + 1}`}
                                          className="h-5 w-5 rounded object-cover border border-gray-200"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ))}
                                      {item.images.length > 4 && (
                                        <span className="text-xs text-gray-500 self-center">+{item.images.length - 4}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                                {item.category}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              KSh {item.price.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {item.owner ? `${item.owner.first_name} ${item.owner.last_name}` : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-sm text-gray-900">{item.rating.toFixed(1)}</span>
                                <div className="ml-1 flex">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <FaStar
                                      key={star}
                                      className={
                                        'h-3 w-3 ' + (star <= item.rating ? 'text-yellow-400' : 'text-gray-300')
                                      }
                                    />
                                  ))}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button 
                                onClick={() => handleViewItemDetails(item)}
                                className="text-green-600 hover:text-green-900"
                                title="View Details"
                              >
                                <FaInfoCircle className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={exportToPDF}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <FaFilePdf className="h-4 w-4 mr-2" />
                  Export PDF
                </button>
                <button
                  onClick={() => setIsViewAllModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Details Modal */}
      {false && isItemDetailModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsItemDetailModalOpen(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Product Details
                  </h3>
                  <button
                    onClick={() => setIsItemDetailModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="h-6 w-6" />
                  </button>
                </div>

                {isLoadingItemDetails ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading product details...</span>
                  </div>
                ) : itemDetails ? (
                  <div className="space-y-6">
                    {/* Product Image */}
                    <div className="flex justify-center">
                      {(() => {
                        const hasValidPhoto = itemDetails.photo && 
                                             itemDetails.photo.trim() !== "" && 
                                             itemDetails.photo !== "null" &&
                                             itemDetails.photo !== "undefined";
                        
                        console.log('🖼️ Item details photo check:', {
                          photo: itemDetails.photo,
                          hasValidPhoto
                        });
                        
                        if (hasValidPhoto) {
                          return (
                            <img
                              src={itemDetails.photo}
                              alt={itemDetails.name}
                              className="h-48 w-48 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                console.log('❌ Item details image failed to load:', itemDetails.photo);
                                e.target.onerror = null;
                                e.target.src = FALLBACK_IMG_192;
                              }}
                              onLoad={() => {
                                console.log('✅ Item details image loaded successfully:', itemDetails.photo);
                              }}
                            />
                          );
                        } else {
                          return (
                            <img
                              src={FALLBACK_IMG_192}
                              alt={itemDetails.name}
                              className="h-48 w-48 object-cover rounded-lg border border-gray-200"
                            />
                          );
                        }
                      })()}
                    </div>

                    {/* Product Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Product ID</h4>
                        <p className="mt-1 text-sm text-gray-900">#{itemDetails.id}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Category</h4>
                        <p className="mt-1 space-x-1">
                          <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                            {itemDetails.category}
                          </span>
                          {/* Sub category removed from details to match backend item shape */}
                        </p>
                      </div>
                      
                      <div className="md:col-span-2">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Product Name</h4>
                        <p className="mt-1 text-lg font-semibold text-gray-900">{itemDetails.name}</p>
                      </div>
                      
                      <div className="md:col-span-2">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Description</h4>
                        <p className="mt-1 text-sm text-gray-900">{itemDetails.description}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Price</h4>
                        <p className="mt-1 text-lg font-bold text-green-600">
                          KSh {parseFloat(itemDetails.price).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Rating</h4>
                        <div className="mt-1 flex items-center">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <FaStar
                                key={star}
                                className={
                                  'h-4 w-4 ' + ((star <= (itemDetails.average_rating || 0)) ? 'text-yellow-400' : 'text-gray-300')
                                }
                              />
                            ))}
                          </div>
                          <span className="ml-2 text-sm text-gray-600">
                            ({itemDetails.average_rating || 0} / 5.0)
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Owner Information */}
                    {itemDetails.owner && (
                      <div className="border-t border-gray-200 pt-6">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Owner Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</h5>
                            <p className="mt-1 text-sm text-gray-900">
                              {itemDetails.owner.first_name} {itemDetails.owner.last_name}
                            </p>
                          </div>
                          
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</h5>
                            <p className="mt-1 text-sm text-gray-900">{itemDetails.owner.email}</p>
                          </div>
                          
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</h5>
                            <p className="mt-1 text-sm text-gray-900">{itemDetails.owner.phone_no}</p>
                          </div>
                          
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">User Type</h5>
                            <p className="mt-1">
                              <span
                                className={
                                  'inline-flex rounded-full px-2 text-xs font-semibold leading-5 ' +
                                  (itemDetails.owner.userType === 'admin'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-green-100 text-green-800')
                                }
                              >
                                {itemDetails.owner.userType}
                              </span>
                            </p>
                          </div>
                          
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email Verified</h5>
                            <p className="mt-1">
                              <span
                                className={
                                  'inline-flex rounded-full px-2 text-xs font-semibold leading-5 ' +
                                  (itemDetails.owner.is_email_verified
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800')
                                }
                              >
                                {itemDetails.owner.is_email_verified ? 'Verified' : 'Not Verified'}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No product details available</p>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setIsItemDetailModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
